// src/components/countries/CountryCard.ts
import "./CountryCard.css";
import type { Country } from "../../types/Country";

/* ============================================================
   MAIN RENDERER — returns HTML string
============================================================ */
export function renderCountryCard(country: Country): string {
  const questions = [
    {
      q: `What is ${country.name} known for?`,
      a: `${country.name} is known for its culture, history, and global influence.`,
    },
    {
      q: `Is ${country.name} safe to visit?`,
      a: `Safety varies by region, but major cities and tourist areas are generally safe.`,
    },
    {
      q: `What language is spoken in ${country.name}?`,
      a: `Primary languages include: ${country.language.join(", ")}.`,
    },
    {
      q: `What is the best time to visit ${country.name}?`,
      a: `The best time to visit depends on climate, festivals, and travel goals.`,
    },
  ];

  const paaHTML = questions
    .map(
      (item, i) => `
      <div class="paa-item" data-index="${i}">
        <div class="paa-question">
          <span>${item.q}</span>
          <span class="paa-arrow">▼</span>
        </div>
        <div class="paa-answer" style="display:none;">
          <p>${item.a}</p>
        </div>
      </div>
    `,
    )
    .join("");

  return `
    <div class="country-card-full">

      <!-- Header -->
      <div class="country-header">
        <h1>${country.name}</h1>
        <p class="continent">${country.continent}</p>
      </div>

      <!-- Two images -->
      <div class="country-image-row">
        <img src="${country.flag}" class="bubble-img" />

        <img
          src="/static/vite/ayesearch/countries/${country.slug}/${country.slug}-1.png"
          class="bubble-img country-fallback"
          data-fallback="https://source.unsplash.com/featured/?${country.name}"
        />
      </div>

      <!-- Main section -->
      <div class="country-main-section">

        <!-- Info grid -->
        <div class="info-grid">
          <div class="info-bubble">
            <h4>Capital</h4>
            <p>${country.capital}</p>
          </div>

          <div class="info-bubble">
            <h4>Largest City</h4>
            <p>${country.capital}</p>
          </div>

          <div class="info-bubble">
            <h4>Currency</h4>
            <p>${country.currency}</p>
          </div>

          <div class="info-bubble">
            <h4>Timezone</h4>
            <p>${country.timezone.join(", ")}</p>
          </div>
        </div>

        <!-- About -->
        <div class="about-card">
          <h3>About ${country.name}</h3>
          <p>${country.description}</p>
        </div>
      </div>

      <hr class="divider" />

      <!-- Bottom 2-column layout -->
      <div class="country-bottom-grid">

        <!-- LEFT COLUMN -->
        <div class="left-col">

          <!-- Wikipedia -->
          <div class="wiki-card">
            <img src="/static/icons/wiki.png" class="wiki-icon" />
            <div>
              <h4>${country.name} — Wikipedia</h4>
              <p>${country.description.slice(0, 150)}...</p>
              <a href="https://en.wikipedia.org/wiki/${country.name}" target="_blank">Read more</a>
            </div>
          </div>

          <!-- Aikipedia -->
          <div class="wiki-card">
            <img src="/static/icons/wiki.png" class="wiki-icon" />
            <div>
              <h4>${country.name} — Aikipedia</h4>
              <p>AI‑generated encyclopedia summary for ${country.name}...</p>
              <a href="https://aikipedia.org/${country.slug}" target="_blank">Explore Aikipedia</a>
            </div>
          </div>

          <!-- People Also Ask -->
          <div class="wiki-card paa-card">
            <h4>People Also Ask</h4>
            ${paaHTML}
          </div>
        </div>

        <!-- RIGHT COLUMN -->
        <div class="right-col">

          <!-- Landmark -->
          <div class="place-card">
            <img
              src="/static/vite/ayesearch/countries/${country.slug}/landmark.png"
              class="place-img country-fallback"
              data-fallback="https://source.unsplash.com/featured/?${country.name} landmark"
            />
            <div class="place-info">
              <h4>Top Landmark</h4>
              <p>Explore iconic sights in ${country.name}.</p>
            </div>
          </div>

          <!-- Destination -->
          <div class="place-card">
            <img
              src="/static/vite/ayesearch/countries/${country.slug}/destination.png"
              class="place-img country-fallback"
              data-fallback="https://source.unsplash.com/featured/?${country.name} tourism"
            />
            <div class="place-info">
              <h4>Popular Destination</h4>
              <p>Discover must‑visit spots.</p>
            </div>
          </div>

          <!-- Hidden Gem -->
          <div class="place-card">
            <img
              src="/static/vite/ayesearch/countries/${country.slug}/gem.png"
              class="place-img country-fallback"
              data-fallback="https://source.unsplash.com/featured/?${country.name} hidden gem"
            />
            <div class="place-info">
              <h4>Hidden Gem</h4>
              <p>Find unique places off the path.</p>
            </div>
          </div>

        </div>
      </div>
    </div>
  `;
}

/* ============================================================
   EVENT INITIALIZER — accordion + image fallbacks
============================================================ */
export function initCountryCardEvents(container: HTMLElement): void {
  // Accordion
  const items = container.querySelectorAll(".paa-item");

  items.forEach((item) => {
    item.addEventListener("click", () => {
      const ans = item.querySelector(".paa-answer") as HTMLElement;
      const arrow = item.querySelector(".paa-arrow") as HTMLElement;

      const open = ans.style.display === "block";
      ans.style.display = open ? "none" : "block";
      arrow.textContent = open ? "▼" : "▲";
    });
  });

  // Image fallbacks
  const imgs = container.querySelectorAll(".country-fallback");

  imgs.forEach((img) => {
    img.addEventListener("error", () => {
      const fallback = img.getAttribute("data-fallback");
      if (fallback) img.setAttribute("src", fallback);
    });
  });
}
